/**
 * Logout route — calls `/.auth/logout` (Easy Auth sign-out endpoint) on mount,
 * then POST /api/auth/logout for completeness, then redirects to /.auth/login/aad.
 *
 * The Easy Auth /.auth/logout endpoint clears the platform-layer session cookie
 * and redirects users back to the Entra sign-in page. A parallel call to
 * /api/auth/logout clears any server-side session state. On failure we still
 * bounce to Entra sign-in since the only useful next action is to re-authenticate.
 *
 * Modified from packages/spa/src/routes/LogoutPage.tsx for Easy Auth.
 */
import { useEffect, useState } from 'react';

import { Skeleton } from '../components/ui/Skeleton.js';

export function LogoutPage(): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function performLogout(): Promise<void> {
      try {
        // Best-effort server-side session cleanup (non-critical).
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Logout request failed';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          // Always navigate to Easy Auth sign-out regardless of API success.
          // /.auth/logout clears the platform-layer session cookie and redirects
          // the user back to the Entra sign-in page.
          setTimeout(() => {
            if (!cancelled) {
              window.location.assign('/.auth/logout');
            }
          }, 250);
        }
      }
    }

    void performLogout();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-50 px-4">
      <div className="w-full max-w-sm space-y-3 rounded-xl border border-ink-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-medium text-ink-700">Signing out…</p>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        {error !== null && (
          <p className="text-xs text-bad-text" role="alert">
            Note: {error}. Redirecting anyway.
          </p>
        )}
      </div>
    </div>
  );
}
